import React, { useState } from 'react';
import { useSimulationStore } from '@/store/useSimulationStore';
import { formatTokens } from '@/utils/formatters';

const Toggle: React.FC<{ label: string; description: string; value: boolean; onChange: (v: boolean) => void; color?: string }> = ({
  label, description, value, onChange, color = '#00E5FF'
}) => (
  <div className="flex items-center justify-between p-3 rounded bg-bg-elevated border border-bg-border">
    <div>
      <div className="font-mono text-sm text-gray-200">{label}</div>
      <div className="font-mono text-xs text-gray-500 mt-0.5">{description}</div>
    </div>
    <button
      onClick={() => onChange(!value)}
      className="relative w-12 h-6 rounded-full transition-all duration-300 flex-shrink-0"
      style={{ background: value ? color + '40' : '#1e2a3a', border: `1px solid ${value ? color : '#1e2a3a'}` }}
    >
      <span
        className="absolute top-0.5 w-5 h-5 rounded-full transition-all duration-300"
        style={{
          left: value ? '26px' : '2px',
          background: value ? color : '#4a5568',
          boxShadow: value ? `0 0 8px ${color}` : 'none',
        }}
      />
    </button>
  </div>
);

export const RulesView: React.FC = () => {
  const { killSwitchActive, toggleKillSwitch, agents } = useSimulationStore();
  const [humanInLoop, setHumanInLoop] = useState({
    ads: true,
    massEmail: true,
    sensitiveReplies: true,
    priceChanges: false,
    productDeletion: true,
  });

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <h1 className="font-pixel text-sm text-gray-200">RÈGLES & GARDE-FOUS</h1>

      {/* Kill Switch global */}
      <div
        className={`rounded-lg border p-4 transition-all duration-300 ${
          killSwitchActive
            ? 'border-neon-red bg-neon-red/10'
            : 'border-bg-border bg-bg-elevated'
        }`}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="font-pixel text-sm text-neon-red mb-1">KILL SWITCH GLOBAL</div>
            <div className="font-mono text-xs text-gray-400">
              {killSwitchActive
                ? '⚠ TOUS LES AGENTS SONT STOPPÉS — Aucune action automatique'
                : 'Désactive immédiatement toute activité autonome des agents'}
            </div>
          </div>
          <button
            onClick={toggleKillSwitch}
            className={`px-6 py-3 rounded font-pixel text-xs font-bold transition-all duration-200 ${
              killSwitchActive
                ? 'bg-neon-red text-white shadow-neon-red animate-pulse'
                : 'border border-neon-red/50 text-neon-red hover:bg-neon-red/10'
            }`}
            style={{ fontSize: '7px' }}
          >
            {killSwitchActive ? '🔴 ACTIF — Cliquer pour relancer' : 'ACTIVER'}
          </button>
        </div>
      </div>

      {/* Human-in-the-loop */}
      <div>
        <div className="font-pixel text-xs text-gray-400 mb-3">HUMAN-IN-THE-LOOP</div>
        <div className="space-y-2">
          <Toggle
            label="Publicité payante"
            description="Validation humaine avant toute création/modification de campagne pub"
            value={humanInLoop.ads}
            onChange={v => setHumanInLoop(s => ({ ...s, ads: v }))}
            color="#E040FB"
          />
          <Toggle
            label="Emails de masse"
            description="Approbation requise pour les envois à plus de 100 contacts"
            value={humanInLoop.massEmail}
            onChange={v => setHumanInLoop(s => ({ ...s, massEmail: v }))}
            color="#E040FB"
          />
          <Toggle
            label="Réponses sensibles"
            description="Litiges, remboursements, escalades client"
            value={humanInLoop.sensitiveReplies}
            onChange={v => setHumanInLoop(s => ({ ...s, sensitiveReplies: v }))}
            color="#FFB300"
          />
          <Toggle
            label="Modification de prix"
            description="Toute variation de prix supérieure à ±20%"
            value={humanInLoop.priceChanges}
            onChange={v => setHumanInLoop(s => ({ ...s, priceChanges: v }))}
            color="#FFB300"
          />
          <Toggle
            label="Suppression de produits"
            description="Empêche la suppression automatique de fiches"
            value={humanInLoop.productDeletion}
            onChange={v => setHumanInLoop(s => ({ ...s, productDeletion: v }))}
            color="#FF6D00"
          />
        </div>
      </div>

      {/* Plafonds budget par agent */}
      <div>
        <div className="font-pixel text-xs text-gray-400 mb-3">PLAFONDS TOKENS PAR AGENT</div>
        <div className="space-y-2">
          {agents.filter(a => a.online).map(agent => (
            <div key={agent.id} className="flex items-center gap-3 p-3 rounded bg-bg-elevated border border-bg-border">
              <span
                className="font-pixel w-16 flex-shrink-0"
                style={{ color: agent.color, fontSize: '6px' }}
              >
                {agent.codename}
              </span>
              <span className="font-mono text-xs text-gray-500 flex-shrink-0">{agent.roleName}</span>
              <div className="flex-1" />
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-gray-600">
                  {formatTokens(agent.tokensBurned)} utilisés
                </span>
                <span className="font-mono text-xs text-gray-700">/</span>
                <input
                  type="number"
                  defaultValue={50000}
                  className="w-20 bg-bg-panel border border-bg-border rounded px-2 py-0.5 font-mono text-xs text-gray-300 text-right"
                />
                <span className="font-mono text-xs text-gray-600">max</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
